import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getUser, logout } from '../utils/auth';
import { 
  BookOpen, Plus, Search, Filter, Edit, Trash2, Eye, Copy, 
  CheckCircle, Clock, Archive, BarChart2, Layers, FileText,
  ChevronDown, X, Save, LogOut, User, Upload, Target, CheckSquare, Square,
  ClipboardList, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const QuestionBankDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  
  // Bulk selection state
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    board_id: '',
    subject_id: '',
    paper_id: '',
    topic_id: '',
    subtopic_id: '',
    question_type: '',
    difficulty_level: '',
    status: '',
    search: ''
  });
  
  // Reference data for filters
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [papers, setPapers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Mark Scheme Import Modal
  const [showMarkSchemeModal, setShowMarkSchemeModal] = useState(false);
  const [markSchemeFile, setMarkSchemeFile] = useState(null);
  const [markSchemeFilters, setMarkSchemeFilters] = useState({
    board_id: '',
    subject_id: '',
    paper_id: '',
    paper_code: '',
    year: '',
    session: '',
    variant: ''
  });
  const [importingMarkScheme, setImportingMarkScheme] = useState(false);
  const [markSchemeResult, setMarkSchemeResult] = useState(null);

  useEffect(() => {
    // Check if user is logged in and has QBM or super_admin role
    if (!user || (user.role !== 'question_bank_manager' && user.role !== 'super_admin')) {
      navigate('/');
      return;
    }
    fetchInitialData();
  }, [user, navigate]);

  useEffect(() => {
    fetchQuestions();
  }, [filters, page]);

  // Fetch filtered topics when board/subject/paper changes
  useEffect(() => {
    if (filters.board_id && filters.subject_id) {
      fetchTopics();
    } else {
      setTopics([]);
      setSubtopics([]);
    }
  }, [filters.board_id, filters.subject_id, filters.paper_id]);

  // Fetch subtopics when topic changes
  useEffect(() => {
    if (filters.topic_id) {
      fetchSubtopics();
    } else {
      setSubtopics([]);
    }
  }, [filters.topic_id]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const fetchInitialData = async () => {
    try {
      const [statsRes, boardsRes, subjectsRes, papersRes] = await Promise.all([
        api.get('/question-bank/stats'),
        api.get('/boards'),
        api.get('/subjects'),
        api.get('/question-bank/papers')
      ]);
      setStats(statsRes.data);
      setBoards(boardsRes.data);
      setSubjects(subjectsRes.data);
      setPapers(papersRes.data);
    } catch (err) {
      console.error('Failed to fetch initial data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.board_id) params.append('board_id', filters.board_id);
      if (filters.subject_id) params.append('subject_id', filters.subject_id);
      if (filters.paper_id) params.append('paper_id', filters.paper_id);
      
      const res = await api.get(`/question-bank/topics?${params}`);
      setTopics(res.data);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  };

  const fetchSubtopics = async () => {
    try {
      const res = await api.get(`/question-bank/subtopics?topic_id=${filters.topic_id}`);
      setSubtopics(res.data);
    } catch (err) {
      console.error('Failed to fetch subtopics:', err);
    }
  };

  const fetchQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          if (key === 'search') {
            params.append('search', value);
          } else {
            params.append(key, value);
          }
        }
      });
      
      const res = await api.get(`/question-bank/questions?${params}`);
      setQuestions(res.data.questions);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      toast.error('Failed to load questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
    
    // Reset dependent filters
    if (key === 'board_id' || key === 'subject_id') {
      setFilters(prev => ({ ...prev, [key]: value, topic_id: '', subtopic_id: '' }));
    } else if (key === 'topic_id') {
      setFilters(prev => ({ ...prev, [key]: value, subtopic_id: '' }));
    }
  };

  const clearFilters = () => {
    setFilters({
      board_id: '',
      subject_id: '',
      paper_id: '',
      topic_id: '',
      subtopic_id: '',
      question_type: '',
      difficulty_level: '',
      status: '',
      search: ''
    });
    setPage(1);
  };

  const handlePublish = async (questionId) => {
    try {
      await api.post(`/question-bank/questions/${questionId}/publish`);
      toast.success('Question published');
      fetchQuestions();
      fetchInitialData();
    } catch (err) {
      toast.error('Failed to publish question');
    }
  };

  const handleDuplicate = async (questionId) => {
    try {
      const res = await api.post(`/question-bank/questions/${questionId}/duplicate`);
      toast.success('Question duplicated');
      navigate(`/qbm/question/${res.data.new_id}/edit`);
    } catch (err) {
      toast.error('Failed to duplicate question');
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to permanently delete this question? This cannot be undone.')) return;
    
    try {
      await api.delete(`/question-bank/questions/${questionId}?permanent=true`);
      toast.success('Question deleted');
      fetchQuestions();
      fetchInitialData();
    } catch (err) {
      toast.error('Failed to delete question');
    }
  };

  const handleDeleteAll = async () => {
    const confirmation = window.prompt(
      'WARNING: This will permanently delete ALL questions in the Question Bank.\n\n' +
      'Type "DELETE ALL" to confirm:'
    );
    
    if (confirmation !== 'DELETE ALL') {
      toast.error('Deletion cancelled - confirmation text did not match');
      return;
    }
    
    try {
      const res = await api.delete('/question-bank/questions/bulk/delete-all?confirm=true');
      toast.success(res.data.message || 'All questions deleted');
      fetchQuestions();
      fetchInitialData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete questions');
    }
  };

  // Bulk selection handlers
  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedQuestions.size === questions.length) {
      setSelectedQuestions(new Set());
    } else {
      setSelectedQuestions(new Set(questions.map(q => q.id)));
    }
  };

  const clearSelection = () => {
    setSelectedQuestions(new Set());
  };

  // Bulk AO update
  const handleBulkAOUpdate = async (newAO) => {
    if (selectedQuestions.size === 0) {
      toast.error('No questions selected');
      return;
    }

    setBulkActionLoading(true);
    try {
      const res = await api.post('/question-bank/questions/bulk/update-ao', {
        question_ids: Array.from(selectedQuestions),
        assessment_objective: newAO
      });
      toast.success(res.data.message || `Updated ${selectedQuestions.size} questions to ${newAO}`);
      clearSelection();
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update questions');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk difficulty update
  const handleBulkDifficultyUpdate = async (newDifficulty) => {
    if (selectedQuestions.size === 0) {
      toast.error('No questions selected');
      return;
    }

    setBulkActionLoading(true);
    try {
      const res = await api.post('/question-bank/questions/bulk/update-difficulty', {
        question_ids: Array.from(selectedQuestions),
        difficulty_level: newDifficulty
      });
      toast.success(res.data.message || `Updated ${selectedQuestions.size} questions`);
      clearSelection();
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update questions');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk publish
  const handleBulkPublish = async () => {
    if (selectedQuestions.size === 0) {
      toast.error('No questions selected');
      return;
    }

    setBulkActionLoading(true);
    try {
      const res = await api.post('/question-bank/questions/bulk/publish', {
        question_ids: Array.from(selectedQuestions)
      });
      toast.success(res.data.message || `Published ${selectedQuestions.size} questions`);
      clearSelection();
      fetchQuestions();
      fetchInitialData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish questions');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Import Mark Scheme to existing questions
  const handleMarkSchemeImport = async () => {
    if (!markSchemeFile) {
      toast.error('Please select a mark scheme PDF');
      return;
    }
    if (!markSchemeFilters.board_id || !markSchemeFilters.subject_id) {
      toast.error('Please select Board and Subject');
      return;
    }

    setImportingMarkScheme(true);
    setMarkSchemeResult(null);
    
    const formData = new FormData();
    formData.append('file', markSchemeFile);
    formData.append('board_id', markSchemeFilters.board_id);
    formData.append('subject_id', markSchemeFilters.subject_id);
    if (markSchemeFilters.paper_id) formData.append('paper_id', markSchemeFilters.paper_id);
    if (markSchemeFilters.paper_code) formData.append('paper_code', markSchemeFilters.paper_code);
    if (markSchemeFilters.year) formData.append('year', markSchemeFilters.year);
    if (markSchemeFilters.session) formData.append('session', markSchemeFilters.session);
    if (markSchemeFilters.variant) formData.append('variant', markSchemeFilters.variant);

    try {
      const res = await api.post('/pdf-import/import-mark-scheme-to-existing', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setMarkSchemeResult(res.data);
      toast.success(res.data.message);
      fetchQuestions(); // Refresh questions to show updated marking schemes
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import mark scheme');
    } finally {
      setImportingMarkScheme(false);
    }
  };

  const resetMarkSchemeModal = () => {
    setShowMarkSchemeModal(false);
    setMarkSchemeFile(null);
    setMarkSchemeFilters({
      board_id: '',
      subject_id: '',
      paper_id: '',
      paper_code: '',
      year: '',
      session: '',
      variant: ''
    });
    setMarkSchemeResult(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-yellow-100 text-yellow-700',
      published: 'bg-green-100 text-green-700',
      archived: 'bg-gray-100 text-gray-700'
    };
    return badges[status] || badges.draft;
  };

  const getDifficultyBadge = (level) => {
    const badges = {
      easy: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      hard: 'bg-red-100 text-red-700'
    };
    return badges[level] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading Question Bank...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Top bar with user info and logout */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <User className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-indigo-200">Welcome,</p>
                <p className="font-semibold">{user?.name || 'Question Bank Manager'}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <BookOpen className="w-8 h-8" />
                Question Bank
              </h1>
              <p className="mt-2 text-indigo-200">Manage your comprehensive question repository</p>
            </div>
            <button
              onClick={() => navigate('/qbm/question/new')}
              className="flex items-center gap-2 px-6 py-3 bg-white text-indigo-700 rounded-lg font-medium hover:bg-indigo-50 transition-all"
              data-testid="create-question-btn"
            >
              <Plus className="w-5 h-5" />
              Add Question
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
              <div className="text-2xl font-bold text-indigo-700">{stats.total_questions}</div>
              <div className="text-sm text-slate-600">Total Questions</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
              <div className="text-2xl font-bold text-green-600">{stats.published}</div>
              <div className="text-sm text-slate-600">Published</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.draft}</div>
              <div className="text-sm text-slate-600">Drafts</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.mcq_questions}</div>
              <div className="text-sm text-slate-600">MCQ</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.structured_questions}</div>
              <div className="text-sm text-slate-600">Structured</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 text-center">
              <div className="text-2xl font-bold text-slate-600">{stats.topics}</div>
              <div className="text-sm text-slate-600">Topics</div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <button
            onClick={() => navigate('/qbm/papers')}
            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left"
          >
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-slate-900">Papers</div>
              <div className="text-sm text-slate-500">Manage papers/components</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/qbm/topics')}
            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left"
          >
            <div className="p-2 bg-green-100 rounded-lg">
              <Layers className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="font-medium text-slate-900">Topics</div>
              <div className="text-sm text-slate-500">Manage topics & subtopics</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/qbm/pdf-import')}
            className="flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-300 hover:border-emerald-400 hover:shadow-md transition-all text-left"
            data-testid="pdf-import-btn"
          >
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Upload className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="font-medium text-emerald-800">Import PDF</div>
              <div className="text-sm text-emerald-600">Import from exam paper</div>
            </div>
          </button>
          <button
            onClick={() => setShowMarkSchemeModal(true)}
            className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border-2 border-amber-300 hover:border-amber-400 hover:shadow-md transition-all text-left"
            data-testid="import-mark-scheme-btn"
          >
            <div className="p-2 bg-amber-100 rounded-lg">
              <ClipboardList className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="font-medium text-amber-800">Import Mark Scheme</div>
              <div className="text-sm text-amber-600">Add to existing questions</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/qbm/import')}
            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left"
          >
            <div className="p-2 bg-purple-100 rounded-lg">
              <Plus className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="font-medium text-slate-900">Bulk Import</div>
              <div className="text-sm text-slate-500">Import questions from CSV</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/qbm/analytics')}
            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left"
          >
            <div className="p-2 bg-orange-100 rounded-lg">
              <BarChart2 className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <div className="font-medium text-slate-900">Analytics</div>
              <div className="text-sm text-slate-500">Usage statistics</div>
            </div>
          </button>
        </div>

        {/* Danger Zone - Delete All */}
        {stats && stats.total_questions > 0 && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-red-800">Danger Zone</h3>
                <p className="text-sm text-red-600">Permanently delete all {stats.total_questions} questions from the Question Bank</p>
              </div>
              <button
                onClick={handleDeleteAll}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
                data-testid="delete-all-questions-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete All Questions
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filter Questions
            </h2>
            {Object.values(filters).some(v => v) && (
              <button
                onClick={clearFilters}
                className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="col-span-2 md:col-span-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full h-10 pl-9 pr-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Board */}
            <select
              value={filters.board_id}
              onChange={(e) => handleFilterChange('board_id', e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Boards</option>
              {boards.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>

            {/* Subject */}
            <select
              value={filters.subject_id}
              onChange={(e) => handleFilterChange('subject_id', e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Subjects</option>
              {subjects.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>

            {/* Paper */}
            <select
              value={filters.paper_id}
              onChange={(e) => handleFilterChange('paper_id', e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Papers</option>
              {papers
                .filter(p => !filters.board_id || p.board_id === filters.board_id)
                .filter(p => !filters.subject_id || p.subject_id === filters.subject_id)
                .map(p => (
                  <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
                ))
              }
            </select>

            {/* Topic */}
            <select
              value={filters.topic_id}
              onChange={(e) => handleFilterChange('topic_id', e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={!filters.board_id || !filters.subject_id}
            >
              <option value="">All Topics</option>
              {topics.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            {/* Subtopic */}
            <select
              value={filters.subtopic_id}
              onChange={(e) => handleFilterChange('subtopic_id', e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={!filters.topic_id}
            >
              <option value="">All Subtopics</option>
              {subtopics.map(st => (
                <option key={st.id} value={st.id}>{st.name}</option>
              ))}
            </select>

            {/* Question Type */}
            <select
              value={filters.question_type}
              onChange={(e) => handleFilterChange('question_type', e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Types</option>
              <option value="mcq">MCQ</option>
              <option value="structured">Structured</option>
            </select>

            {/* Difficulty */}
            <select
              value={filters.difficulty_level}
              onChange={(e) => handleFilterChange('difficulty_level', e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Difficulties</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>

            {/* Status */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions Toolbar */}
        {selectedQuestions.size > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckSquare className="w-5 h-5 text-indigo-600" />
                <span className="font-medium text-indigo-900">
                  {selectedQuestions.size} question{selectedQuestions.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={clearSelection}
                  className="text-sm text-indigo-600 hover:text-indigo-800 underline"
                >
                  Clear selection
                </button>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Assessment Objective Dropdown */}
                <div className="relative group">
                  <button
                    disabled={bulkActionLoading}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-indigo-300 rounded-lg text-indigo-700 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                    data-testid="bulk-ao-dropdown"
                  >
                    <Target className="w-4 h-4" />
                    Set AO
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[120px]">
                    <button
                      onClick={() => handleBulkAOUpdate('AO1')}
                      className="w-full px-4 py-2 text-left hover:bg-indigo-50 text-sm flex items-center gap-2"
                      data-testid="bulk-ao1-btn"
                    >
                      <span className="w-8 h-5 bg-blue-100 text-blue-700 rounded text-xs flex items-center justify-center">AO1</span>
                      Knowledge
                    </button>
                    <button
                      onClick={() => handleBulkAOUpdate('AO2')}
                      className="w-full px-4 py-2 text-left hover:bg-indigo-50 text-sm flex items-center gap-2"
                      data-testid="bulk-ao2-btn"
                    >
                      <span className="w-8 h-5 bg-green-100 text-green-700 rounded text-xs flex items-center justify-center">AO2</span>
                      Application
                    </button>
                    <button
                      onClick={() => handleBulkAOUpdate('AO3')}
                      className="w-full px-4 py-2 text-left hover:bg-indigo-50 text-sm flex items-center gap-2"
                      data-testid="bulk-ao3-btn"
                    >
                      <span className="w-8 h-5 bg-purple-100 text-purple-700 rounded text-xs flex items-center justify-center">AO3</span>
                      Analysis
                    </button>
                  </div>
                </div>

                {/* Difficulty Dropdown */}
                <div className="relative group">
                  <button
                    disabled={bulkActionLoading}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-indigo-300 rounded-lg text-indigo-700 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                    data-testid="bulk-difficulty-dropdown"
                  >
                    <Layers className="w-4 h-4" />
                    Set Difficulty
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[120px]">
                    <button
                      onClick={() => handleBulkDifficultyUpdate('Easy')}
                      className="w-full px-4 py-2 text-left hover:bg-green-50 text-sm"
                      data-testid="bulk-easy-btn"
                    >
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Easy
                    </button>
                    <button
                      onClick={() => handleBulkDifficultyUpdate('Medium')}
                      className="w-full px-4 py-2 text-left hover:bg-yellow-50 text-sm"
                      data-testid="bulk-medium-btn"
                    >
                      <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                      Medium
                    </button>
                    <button
                      onClick={() => handleBulkDifficultyUpdate('Hard')}
                      className="w-full px-4 py-2 text-left hover:bg-red-50 text-sm"
                      data-testid="bulk-hard-btn"
                    >
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      Hard
                    </button>
                  </div>
                </div>

                {/* Bulk Publish */}
                <button
                  onClick={handleBulkPublish}
                  disabled={bulkActionLoading}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  data-testid="bulk-publish-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  Publish Selected
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Questions List */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {questions.length > 0 && (
                <button
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-indigo-600"
                  data-testid="select-all-btn"
                >
                  {selectedQuestions.size === questions.length ? (
                    <CheckSquare className="w-4 h-4 text-indigo-600" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  {selectedQuestions.size === questions.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
              <h2 className="text-lg font-semibold text-slate-900">
                Questions ({total})
              </h2>
            </div>
          </div>

          {loadingQuestions ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-slate-600">Loading questions...</p>
            </div>
          ) : questions.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No questions found. Try adjusting your filters or create a new question.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {questions.map(q => (
                <div 
                  key={q.id} 
                  className={`p-4 hover:bg-slate-50 transition-colors ${selectedQuestions.has(q.id) ? 'bg-indigo-50' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => handleSelectQuestion(q.id)}
                      className="mt-1 flex-shrink-0"
                      data-testid={`select-question-${q.id}`}
                    >
                      {selectedQuestions.has(q.id) ? (
                        <CheckSquare className="w-5 h-5 text-indigo-600" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-400 hover:text-indigo-600" />
                      )}
                    </button>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                          {q.question_code || `Q${q.question_number}`}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusBadge(q.status)}`}>
                          {q.status}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${q.question_type === 'mcq' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {q.question_type === 'mcq' ? 'MCQ' : 'Structured'}
                        </span>
                        {/* Assessment Objective badge */}
                        {q.assessment_objective && (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            q.assessment_objective === 'AO1' ? 'bg-blue-100 text-blue-700' :
                            q.assessment_objective === 'AO2' ? 'bg-green-100 text-green-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {q.assessment_objective}
                          </span>
                        )}
                        {(q.difficulty_level || q.difficulty) && (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getDifficultyBadge(q.difficulty_level || q.difficulty)}`}>
                            {q.difficulty_level || q.difficulty}
                          </span>
                        )}
                        <span className="text-xs text-slate-500">
                          {q.total_marks || q.marks || 0} marks
                        </span>
                      </div>
                      
                      <p className="text-slate-900 mb-2 line-clamp-2">{q.question_text}</p>
                      
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>{q.board_name || q.board || 'Unknown Board'}</span>
                        <span>•</span>
                        <span>{q.subject_name || q.subject || 'Unknown Subject'}</span>
                        <span>•</span>
                        <span>{q.paper_code || q.paper || 'Unknown Paper'}</span>
                        {(q.topic_name || q.topic) && (
                          <>
                            <span>•</span>
                            <span>{q.topic_name || q.topic}</span>
                          </>
                        )}
                        {(q.subtopic_name || q.subtopic) && (
                          <>
                            <span>›</span>
                            <span>{q.subtopic_name || q.subtopic}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => navigate(`/qbm/question/${q.id}`)}
                        className="p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/qbm/question/${q.id}/edit`)}
                        className="p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(q.id)}
                        className="p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Duplicate"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {q.status === 'draft' && (
                        <button
                          onClick={() => handlePublish(q.id)}
                          className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Publish"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(q.id)}
                        className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Archive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-slate-200 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Page {page} of {totalPages} ({total} total)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mark Scheme Import Modal */}
      {showMarkSchemeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <ClipboardList className="w-6 h-6 text-amber-600" />
                  Import Mark Scheme
                </h2>
                <button
                  onClick={resetMarkSchemeModal}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-slate-600 mt-2">
                Upload an IGCSE mark scheme PDF to link marking points to existing questions in the Question Bank.
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Mark Scheme PDF *
                </label>
                {!markSchemeFile ? (
                  <label className="flex items-center justify-center gap-2 p-6 border-2 border-dashed border-amber-300 rounded-lg cursor-pointer hover:bg-amber-50 transition-all">
                    <Upload className="w-6 h-6 text-amber-600" />
                    <span className="text-amber-700 font-medium">Click to upload PDF</span>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setMarkSchemeFile(e.target.files[0])}
                      className="hidden"
                      data-testid="mark-scheme-file-input"
                    />
                  </label>
                ) : (
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-amber-600" />
                      <span className="font-medium text-amber-900">{markSchemeFile.name}</span>
                    </div>
                    <button
                      onClick={() => setMarkSchemeFile(null)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Filter Selectors */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Board *
                  </label>
                  <select
                    value={markSchemeFilters.board_id}
                    onChange={(e) => setMarkSchemeFilters(prev => ({ ...prev, board_id: e.target.value, subject_id: '' }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="ms-board-select"
                  >
                    <option value="">Select Board</option>
                    {boards.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Subject *
                  </label>
                  <select
                    value={markSchemeFilters.subject_id}
                    onChange={(e) => setMarkSchemeFilters(prev => ({ ...prev, subject_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="ms-subject-select"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Paper Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Paper <span className="text-red-500">*</span>
                </label>
                <select
                  value={markSchemeFilters.paper_id}
                  onChange={(e) => setMarkSchemeFilters(prev => ({ ...prev, paper_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                  data-testid="ms-paper-select"
                >
                  <option value="">Select Paper</option>
                  {papers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={markSchemeFilters.year}
                    onChange={(e) => setMarkSchemeFilters(prev => ({ ...prev, year: e.target.value }))}
                    placeholder="e.g., 2024"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="ms-year"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Session <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={markSchemeFilters.session}
                    onChange={(e) => setMarkSchemeFilters(prev => ({ ...prev, session: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="ms-session"
                  >
                    <option value="">Select Session</option>
                    <option value="May/June">May/June</option>
                    <option value="Oct/Nov">Oct/Nov</option>
                    <option value="Feb/Mar">Feb/Mar</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Variant <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={markSchemeFilters.variant}
                    onChange={(e) => setMarkSchemeFilters(prev => ({ ...prev, variant: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500"
                    data-testid="ms-variant"
                  >
                    <option value="">Select Variant</option>
                    <option value="1">1 (e.g., 0625/41)</option>
                    <option value="2">2 (e.g., 0625/42)</option>
                    <option value="3">3 (e.g., 0625/43)</option>
                  </select>
                </div>
              </div>
              
              {/* Help text */}
              <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                <strong>Paper Code Format:</strong> 0625/43 = Syllabus (0625) + Paper (4) + Variant (3). 
                Each variant has different questions.
              </p>

              {/* Result Display */}
              {markSchemeResult && (
                <div className={`p-4 rounded-lg border ${markSchemeResult.questions_updated > 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className={`flex items-center gap-2 font-medium mb-2 ${markSchemeResult.questions_updated > 0 ? 'text-green-800' : 'text-amber-800'}`}>
                    <CheckCircle className="w-5 h-5" />
                    {markSchemeResult.message}
                  </div>
                  <div className={`text-sm space-y-1 ${markSchemeResult.questions_updated > 0 ? 'text-green-700' : 'text-amber-700'}`}>
                    <p>Schemes found in PDF: {markSchemeResult.schemes_found}</p>
                    <p>Questions in QB: {markSchemeResult.questions_in_qb || 'N/A'}</p>
                    <p>Questions updated: {markSchemeResult.questions_updated}</p>
                  </div>
                  
                  {/* Debug info when no matches */}
                  {markSchemeResult.questions_updated === 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-200 space-y-2">
                      <p className="text-xs text-amber-800 font-medium">Debug Info:</p>
                      {markSchemeResult.qb_question_numbers?.length > 0 && (
                        <div>
                          <p className="text-xs text-amber-700">Question numbers in QB: {markSchemeResult.qb_question_numbers.join(', ')}</p>
                        </div>
                      )}
                      {markSchemeResult.ms_question_numbers?.length > 0 && (
                        <div>
                          <p className="text-xs text-amber-700">Question numbers in Mark Scheme: {markSchemeResult.ms_question_numbers.join(', ')}</p>
                        </div>
                      )}
                      {markSchemeResult.unmatched_samples?.length > 0 && (
                        <div>
                          <p className="text-xs text-amber-700">Unmatched schemes (samples):</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {markSchemeResult.unmatched_samples.slice(0, 5).map((s, i) => (
                              <span key={i} className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                                Q{s.question_number}{s.part && `(${s.part})`}{s.sub_part && `(${s.sub_part})`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {markSchemeResult.linked_details?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-green-200">
                      <p className="text-xs text-green-700 font-medium mb-1">Linked schemes ({markSchemeResult.schemes_linked || markSchemeResult.linked_details.length}):</p>
                      <div className="flex flex-wrap gap-1">
                        {markSchemeResult.linked_details.slice(0, 10).map((d, i) => (
                          <span key={i} className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded" title={d.marking_preview || ''}>
                            Q{d.question_number}{d.part && `(${d.part})`}{d.sub_part && `(${d.sub_part})`}
                          </span>
                        ))}
                        {markSchemeResult.linked_details.length > 10 && (
                          <span className="text-xs text-green-700">+{markSchemeResult.linked_details.length - 10} more</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Debug Trace - Always show for troubleshooting */}
                  {markSchemeResult.debug_trace?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <details className="text-xs">
                        <summary className="cursor-pointer text-slate-600 font-medium hover:text-slate-800">
                          Debug Trace ({markSchemeResult.debug_trace.length} schemes processed)
                        </summary>
                        <div className="mt-2 max-h-40 overflow-y-auto space-y-1 bg-slate-50 p-2 rounded">
                          {markSchemeResult.debug_trace.slice(0, 15).map((t, i) => (
                            <div key={i} className={`text-xs px-2 py-1 rounded ${t.match_status?.startsWith('SUCCESS') ? 'bg-green-100 text-green-800' : t.match_status?.startsWith('FAIL') ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                              <span className="font-medium">{t.scheme}</span>: {t.match_status}
                              {t.q_parts_labels && <span className="ml-2 text-slate-500">[parts: {t.q_parts_labels.join(',')}]</span>}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}
                  
                  {/* QB Structure Debug */}
                  {markSchemeResult.debug_qb_structure?.length > 0 && (
                    <div className="mt-2">
                      <details className="text-xs">
                        <summary className="cursor-pointer text-slate-600 font-medium hover:text-slate-800">
                          Question Structure in QB (first 5)
                        </summary>
                        <div className="mt-2 max-h-32 overflow-y-auto bg-slate-50 p-2 rounded">
                          {markSchemeResult.debug_qb_structure.map((q, i) => (
                            <div key={i} className="text-xs text-slate-700">
                              Q{q.q_num}: {q.parts?.map(p => `(${p.label})[${p.sub_parts?.join(',') || 'no sub'}]`).join(' ')}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={resetMarkSchemeModal}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                {markSchemeResult ? 'Close' : 'Cancel'}
              </button>
              {!markSchemeResult && (
                <button
                  onClick={handleMarkSchemeImport}
                  disabled={importingMarkScheme || !markSchemeFile || !markSchemeFilters.board_id || !markSchemeFilters.subject_id || !markSchemeFilters.paper_id || !markSchemeFilters.year || !markSchemeFilters.session || !markSchemeFilters.variant}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="import-mark-scheme-submit"
                >
                  {importingMarkScheme ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Import Mark Scheme
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBankDashboard;
